import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { getShapeById } from '../helpers/shape-list'
import { expandGroupDescendants } from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useShapeLayering(ctx: ShapeManagerStateApi) {
  const { selectedShapeIdsRef, setShapes, shapesRef } = ctx

  const reorderShapes = useCallback(
    (newOrder: string[]) => {
      setShapes((prev) => {
        const byId = new Map(prev.map((s) => [s.id, s]))
        const next = newOrder.map((id) => byId.get(id)).filter((s): s is ShapeData => s != null)

        shapesRef.current = next

        return next
      })
    },
    [setShapes, shapesRef]
  )

  const bringToFront = useCallback((): { beforeOrder: string[]; afterOrder: string[] } | null => {
    const ids = selectedShapeIdsRef.current

    if (ids.length === 0) return null
    if (ids.some((id) => getShapeById(shapesRef.current, id)?.locked === true)) return null
    const expandedIds = expandGroupDescendants(ids, shapesRef.current)
    const expandedSet = new Set(expandedIds)
    const beforeOrder = shapesRef.current.map((s) => s.id)

    // Compute the new order immediately from shapesRef.current so afterOrder is always available
    // in the same event tick regardless of React batching (same pattern as updateShape line 1997).
    const applyReorder = (list: ShapeData[]) => {
      const notSelected = list.filter((s) => !expandedSet.has(s.id))
      const selected = list.filter((s) => expandedSet.has(s.id))

      return [...notSelected, ...selected]
    }
    const immediateNext = applyReorder(shapesRef.current)
    const afterOrder = immediateNext.map((s) => s.id)

    shapesRef.current = immediateNext

    setShapes((prev) => {
      const next = applyReorder(prev)

      shapesRef.current = next
      return next
    })

    // Check if order actually changed
    if (beforeOrder.join(',') === afterOrder.join(',')) return null

    return { beforeOrder, afterOrder }
  }, [selectedShapeIdsRef, setShapes, shapesRef])

  const sendToBack = useCallback((): { beforeOrder: string[]; afterOrder: string[] } | null => {
    const ids = selectedShapeIdsRef.current

    if (ids.length === 0) return null
    if (ids.some((id) => getShapeById(shapesRef.current, id)?.locked === true)) return null
    const expandedIds = expandGroupDescendants(ids, shapesRef.current)
    const expandedSet = new Set(expandedIds)
    const beforeOrder = shapesRef.current.map((s) => s.id)

    // Compute the new order immediately from shapesRef.current so afterOrder is always available
    // in the same event tick regardless of React batching (same pattern as updateShape line 1997).
    const applyReorder = (list: ShapeData[]) => {
      const notSelected = list.filter((s) => !expandedSet.has(s.id))
      const selected = list.filter((s) => expandedSet.has(s.id))

      return [...selected, ...notSelected]
    }
    const immediateNext = applyReorder(shapesRef.current)
    const afterOrder = immediateNext.map((s) => s.id)

    shapesRef.current = immediateNext

    setShapes((prev) => {
      const next = applyReorder(prev)

      shapesRef.current = next
      return next
    })

    // Check if order actually changed
    if (beforeOrder.join(',') === afterOrder.join(',')) return null

    return { beforeOrder, afterOrder }
  }, [selectedShapeIdsRef, setShapes, shapesRef])

  const bringForward = useCallback((): { beforeOrder: string[]; afterOrder: string[] } | null => {
    const ids = selectedShapeIdsRef.current

    if (ids.length === 0) return null
    if (ids.some((id) => getShapeById(shapesRef.current, id)?.locked === true)) return null
    const expandedIds = expandGroupDescendants(ids, shapesRef.current)
    const expandedSet = new Set(expandedIds)
    const beforeOrder = shapesRef.current.map((s) => s.id)

    // Compute the new order immediately from shapesRef.current so afterOrder is always available
    // in the same event tick regardless of React batching (same pattern as updateShape line 1997).
    const applyReorder = (list: ShapeData[]) => {
      const next = [...list]

      // Find the highest index of any unit shape (group shell + all descendants)
      let unitHighIndex = -1

      for (let i = 0; i < next.length; i++) {
        if (expandedSet.has(next[i]!.id)) {
          unitHighIndex = i
        }
      }

      if (unitHighIndex < 0) return next

      // Find the first non-unit shape above the highest unit shape
      let firstAboveIndex = -1

      for (let i = unitHighIndex + 1; i < next.length; i++) {
        if (!expandedSet.has(next[i]!.id)) {
          firstAboveIndex = i
          break
        }
      }

      if (firstAboveIndex < 0) return next

      // Remove that shape and re-insert it just before the lowest unit shape
      const [shapeToMove] = next.splice(firstAboveIndex, 1)

      let unitLowIndex = next.length

      for (let i = 0; i < next.length; i++) {
        if (expandedSet.has(next[i]!.id)) {
          unitLowIndex = i
          break
        }
      }

      next.splice(unitLowIndex, 0, shapeToMove!)

      return next
    }
    const immediateNext = applyReorder(shapesRef.current)
    const afterOrder = immediateNext.map((s) => s.id)

    shapesRef.current = immediateNext

    setShapes((prev) => {
      const next = applyReorder(prev)

      shapesRef.current = next
      return next
    })

    // Check if order actually changed
    if (beforeOrder.join(',') === afterOrder.join(',')) return null

    return { beforeOrder, afterOrder }
  }, [selectedShapeIdsRef, setShapes, shapesRef])

  const sendBackward = useCallback((): { beforeOrder: string[]; afterOrder: string[] } | null => {
    const ids = selectedShapeIdsRef.current

    if (ids.length === 0) return null
    if (ids.some((id) => getShapeById(shapesRef.current, id)?.locked === true)) return null
    const expandedIds = expandGroupDescendants(ids, shapesRef.current)
    const expandedSet = new Set(expandedIds)
    const beforeOrder = shapesRef.current.map((s) => s.id)

    // Compute the new order immediately from shapesRef.current so afterOrder is always available
    // in the same event tick regardless of React batching (same pattern as updateShape line 1997).
    const applyReorder = (list: ShapeData[]) => {
      const next = [...list]

      // Find the lowest index of any unit shape (group shell + all descendants)
      let unitLowIndex = next.length

      for (let i = 0; i < next.length; i++) {
        if (expandedSet.has(next[i]!.id)) {
          unitLowIndex = i
          break
        }
      }

      if (unitLowIndex >= next.length) return next

      // Find the last non-unit shape below the lowest unit shape
      let lastBelowIndex = -1

      for (let i = unitLowIndex - 1; i >= 0; i--) {
        if (!expandedSet.has(next[i]!.id)) {
          lastBelowIndex = i
          break
        }
      }

      if (lastBelowIndex < 0) return next

      // Remove that shape and re-insert it just after the highest unit shape
      const [shapeToMove] = next.splice(lastBelowIndex, 1)

      let unitHighIndex = -1

      for (let i = 0; i < next.length; i++) {
        if (expandedSet.has(next[i]!.id)) {
          unitHighIndex = i
        }
      }

      if (unitHighIndex < 0) {
        next.push(shapeToMove!)
      } else {
        next.splice(unitHighIndex + 1, 0, shapeToMove!)
      }

      return next
    }
    const immediateNext = applyReorder(shapesRef.current)
    const afterOrder = immediateNext.map((s) => s.id)

    shapesRef.current = immediateNext

    setShapes((prev) => {
      const next = applyReorder(prev)

      shapesRef.current = next
      return next
    })

    // Check if order actually changed
    if (beforeOrder.join(',') === afterOrder.join(',')) return null

    return { beforeOrder, afterOrder }
  }, [selectedShapeIdsRef, setShapes, shapesRef])

  const canBringForward = useCallback(() => {
    const ids = selectedShapeIdsRef.current
    const shapes = shapesRef.current

    if (ids.length === 0 || shapes.length === 0) return false
    if (ids.some((id) => getShapeById(shapes, id)?.locked === true)) return false

    const expandedIds = expandGroupDescendants(ids, shapes)
    const expandedSet = new Set(expandedIds)

    // Find the highest index of any expanded shape (group shell + all descendants)
    let highestSelectedIndex = -1

    for (let i = 0; i < shapes.length; i++) {
      if (expandedSet.has(shapes[i].id)) {
        highestSelectedIndex = i
      }
    }

    // Can bring forward if there's at least one non-expanded shape above the highest expanded
    for (let i = highestSelectedIndex + 1; i < shapes.length; i++) {
      if (!expandedSet.has(shapes[i].id)) {
        return true
      }
    }

    return false
  }, [selectedShapeIdsRef, shapesRef])

  const canSendBackward = useCallback(() => {
    const ids = selectedShapeIdsRef.current
    const shapes = shapesRef.current

    if (ids.length === 0 || shapes.length === 0) return false
    if (ids.some((id) => getShapeById(shapes, id)?.locked === true)) return false

    const expandedIds = expandGroupDescendants(ids, shapes)
    const expandedSet = new Set(expandedIds)

    // Find the lowest index of any expanded shape (group shell + all descendants)
    let lowestSelectedIndex = shapes.length

    for (let i = 0; i < shapes.length; i++) {
      if (expandedSet.has(shapes[i].id)) {
        lowestSelectedIndex = i
        break
      }
    }

    // Can send backward if there's at least one non-expanded shape below the lowest expanded
    for (let i = 0; i < lowestSelectedIndex; i++) {
      if (!expandedSet.has(shapes[i].id)) {
        return true
      }
    }

    return false
  }, [selectedShapeIdsRef, shapesRef])

  return {
    reorderShapes,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    canBringForward,
    canSendBackward,
  }
}

export type ShapeLayeringApi = ReturnType<typeof useShapeLayering>
